// src/pages/LeaderReports.jsx - Rendimiento y Reportes
// Rediseño visual aplicado 2026-05-13. Lógica preservada 1:1 (debounce 5s + silent
// refetch por socket — crítico para 1500 users).
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import {
    BarChart, Bar, LineChart, Line,
    XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell,
} from 'recharts';
import api from '../services/api';
import socket from '../services/socket';
import { AuthContext } from '../context/AuthContext';
import UserProfileModal from '../components/UserProfileModal';
import Skeleton, { SkeletonKpi, SkeletonRow } from '../components/Skeleton';


// ── Iconos SVG inline ────────────────────────────────────────────────────────
const Icon = ({ children, size = 16, stroke = 1.75 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth={stroke}
         strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        {children}
    </svg>
);
const I = {
    refresh: <Icon size={14}><path d="M3 12a9 9 0 0 1 15-6.7L21 8" /><path d="M21 3v5h-5" /><path d="M21 12a9 9 0 0 1-15 6.7L3 16" /><path d="M3 21v-5h5" /></Icon>,
    chev:    <Icon size={14}><path d="m9 18 6-6-6-6" /></Icon>,
    chevL:   <Icon size={14}><path d="m15 18-6-6 6-6" /></Icon>,
    trophy:  <Icon size={14}><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" /><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" /><path d="M4 22h16M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22h10c0-1.76-.85-3.25-2.03-3.79C14.47 17.98 14 17.55 14 17v-2.34" /><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" /></Icon>,
};

const PRIO_COLOR = { low: '#059669', medium: '#2563eb', high: '#d97706', urgent: '#dc2626' };
const PRIO_LABEL = { low: 'Baja', medium: 'Media', high: 'Alta', urgent: 'Urgente' };
const BRAND_BLUE = '#0a4aa8';


// ── Helpers ──────────────────────────────────────────────────────────────────
function fmtHours(h) {
    if (h == null || h <= 0) return '—';
    if (h < 1)  return '< 1 h';
    if (h < 24) return `${Math.round(h)} h`;
    return `${(h / 24).toFixed(1)} días`;
}
function fmtCsat(v) { return v != null ? v.toFixed(1) + ' ★' : '—'; }
function fmtDate(iso) {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
}
function fmtMonth(ym) {
    const [y, m] = ym.split('-');
    const names = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
    return `${names[parseInt(m, 10) - 1]} ${y}`;
}
const CDMX_OFFSET_MS = 6 * 60 * 60 * 1000;
function isoToCdmxDate(iso) {
    if (!iso) return '';
    return new Date(new Date(iso).getTime() - CDMX_OFFSET_MS).toISOString().slice(0, 10);
}
function fillDays(trend, fromIso, toIso) {
    const map = {};
    (trend || []).forEach(d => { map[d.date] = d.count; });
    const result = [];
    const fromKey = isoToCdmxDate(fromIso);
    const toKey   = isoToCdmxDate(toIso);
    if (!fromKey || !toKey) return result;
    let cur = fromKey;
    while (cur <= toKey) {
        result.push({ date: cur.slice(5), count: map[cur] || 0 });
        const d = new Date(cur + 'T12:00:00Z');
        d.setUTCDate(d.getUTCDate() + 1);
        cur = d.toISOString().slice(0, 10);
    }
    return result;
}

const PRESETS = [
    { label: 'Hoy',    key: 'today'  },
    { label: 'Semana', key: 'week'   },
    { label: 'Mes',    key: 'month'  },
    { label: 'Rango',  key: 'custom' },
];

function getPresetDates(key) {
    const now   = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    switch (key) {
        case 'today':
            return { from: today, to: new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999) };
        case 'week': {
            const f = new Date(today);
            f.setDate(f.getDate() - 6);
            return { from: f, to: new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999) };
        }
        case 'month':
            return {
                from: new Date(now.getFullYear(), now.getMonth(), 1),
                to:   new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999),
            };
        default:
            return null;
    }
}


// ── Scoped CSS ───────────────────────────────────────────────────────────────
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
.lr-root {
    --lr-accent: var(--brand-700, #0a4aa8);
    --lr-accent-hover: var(--brand-800, #07357f);
    --lr-accent-light: #dbeafe;
    --lr-accent-ring: rgba(10, 74, 168, .12);
    --lr-shadow-sm: 0 1px 2px rgba(15,23,42,.04), 0 2px 8px rgba(15,23,42,.04);
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
    -webkit-font-smoothing: antialiased;
    max-width: 1280px; margin: 0 auto; padding: 24px 20px 60px;
    color: var(--c-text-1);
}
.lr-root * { box-sizing: border-box; }

.lr-header {
    display: flex; justify-content: space-between; align-items: flex-start;
    margin-bottom: 20px; flex-wrap: wrap; gap: 14px;
}
.lr-title { font-size: 24px; font-weight: 700; margin: 0 0 4px; letter-spacing: -0.01em; }
.lr-subtitle { color: var(--c-text-3); font-size: 13.5px; margin: 0; line-height: 1.5; }
.lr-period {
    color: var(--c-text-4); font-size: 12px; margin-left: 8px;
}

.lr-actions { display: flex; gap: 8px; align-items: center; }

.lr-btn {
    display: inline-flex; align-items: center; gap: 6px;
    border: 1px solid var(--c-border);
    background: var(--c-card);
    color: var(--c-text-2);
    border-radius: 8px;
    padding: 7px 14px;
    font-size: 13px; font-weight: 600;
    cursor: pointer; font-family: inherit;
    transition: all .15s;
}
.lr-btn:hover:not(:disabled) { background: var(--c-msg-bg); border-color: var(--c-text-3); }
.lr-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.lr-btn-primary {
    background: var(--lr-accent); color: #fff; border-color: var(--lr-accent);
    box-shadow: 0 1px 3px rgba(10,74,168,.25);
}
.lr-btn-primary:hover:not(:disabled) { background: var(--lr-accent-hover); border-color: var(--lr-accent-hover); }

/* Filtros temporal */
.lr-filters {
    background: var(--c-card); border: 1px solid var(--c-border);
    border-radius: 12px;
    box-shadow: var(--lr-shadow-sm);
    padding: 12px 16px; margin-bottom: 22px;
    display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
}
.lr-presets { display: inline-flex; gap: 4px; padding: 3px; background: var(--c-input-bg); border: 1px solid var(--c-border); border-radius: 999px; }
.lr-preset {
    padding: 6px 16px; border-radius: 999px; border: none; background: transparent;
    font-size: 13px; font-weight: 600; color: var(--c-text-3);
    cursor: pointer; font-family: inherit;
    transition: all .15s;
}
.lr-preset:hover { color: var(--c-text-1); }
.lr-preset.active { background: var(--lr-accent); color: #fff; }
.lr-preset.active:hover { background: var(--lr-accent-hover); color: #fff; }

.lr-custom-range { display: flex; align-items: center; gap: 6px; }
.lr-date {
    padding: 6px 10px; font-size: 13px;
    border: 1px solid var(--c-border); border-radius: 8px;
    background: var(--c-input-bg); color: var(--c-text-1);
    font-family: inherit; outline: none; width: 150px;
    transition: border-color .15s, box-shadow .15s;
}
.lr-date:focus { border-color: var(--lr-accent); box-shadow: 0 0 0 3px var(--lr-accent-ring); }

/* KPI grid */
.lr-kpi-grid {
    display: grid; grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
    gap: 14px; margin-bottom: 26px;
}
.lr-kpi {
    background: var(--c-card); border: 1px solid var(--c-border);
    border-left: 4px solid var(--kpi-color, var(--lr-accent));
    border-radius: 12px;
    padding: 16px 18px;
    box-shadow: var(--lr-shadow-sm);
    transition: transform .15s, box-shadow .15s;
}
.lr-kpi.clickable { cursor: pointer; }
.lr-kpi.clickable:hover, .lr-kpi:hover { transform: translateY(-2px); box-shadow: 0 4px 16px rgba(15,23,42,.06); }
.lr-kpi-label {
    font-size: 11px; font-weight: 600; letter-spacing: 0.5px;
    text-transform: uppercase; color: var(--c-text-4); margin: 0 0 6px;
}
.lr-kpi-value {
    font-size: 28px; font-weight: 800; color: var(--c-text-1);
    margin: 0; line-height: 1; letter-spacing: -0.01em;
    font-variant-numeric: tabular-nums;
}
.lr-kpi-sub { font-size: 11px; color: var(--c-text-4); margin: 5px 0 0; }
.lr-kpi.clickable .lr-kpi-sub { color: var(--lr-accent); }

/* Cards y secciones */
.lr-card {
    background: var(--c-card); border: 1px solid var(--c-border);
    border-radius: 12px;
    box-shadow: var(--lr-shadow-sm);
    overflow: hidden;
    margin-bottom: 16px;
}
.lr-card-head {
    padding: 14px 20px;
    border-bottom: 1px solid var(--c-card-header-border);
    display: flex; align-items: center; justify-content: space-between; gap: 8px; flex-wrap: wrap;
}
.lr-card-title {
    font-size: 13px; font-weight: 600; color: var(--c-text-2);
    margin: 0;
}
.lr-card-title-sub {
    font-size: 11px; color: var(--c-text-4); font-weight: 400; margin-left: 8px;
}
.lr-card-body { padding: 16px 14px 12px; }
.lr-card-body.flush { padding: 0; }
.lr-card-body.tight { padding: 14px 20px; }

.lr-cols-2 {
    display: grid; grid-template-columns: minmax(0, 5fr) minmax(0, 7fr);
    gap: 14px; margin-bottom: 16px;
}
@media (max-width: 920px) { .lr-cols-2 { grid-template-columns: 1fr; } }

.lr-cols-eq {
    display: grid; grid-template-columns: minmax(0, 7fr) minmax(0, 5fr);
    gap: 14px; margin-bottom: 16px;
}
@media (max-width: 920px) { .lr-cols-eq { grid-template-columns: 1fr; } }

/* Table */
.lr-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.lr-table thead th {
    background: var(--c-msg-bg);
    padding: 10px 14px;
    text-align: left;
    font-size: 11px;
    font-weight: 700;
    color: var(--c-text-4);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-bottom: 1px solid var(--c-card-header-border);
    white-space: nowrap;
}
.lr-table tbody td {
    padding: 11px 14px;
    border-bottom: 1px solid var(--c-card-header-border);
    color: var(--c-text-2);
    vertical-align: middle;
}
.lr-table tbody tr:last-child td { border-bottom: none; }
.lr-table tbody tr:nth-child(even) td { background: rgba(0,0,0,.015); }
[data-bs-theme="dark"] .lr-table tbody tr:nth-child(even) td { background: rgba(255,255,255,.02); }
.lr-table .center { text-align: center; }
.lr-table .right { text-align: right; }
.lr-table .strong { color: var(--c-text-1); font-weight: 600; }
.lr-table .num { font-variant-numeric: tabular-nums; }

.lr-user-link {
    background: none; border: none; padding: 0; margin: 0;
    color: inherit; font: inherit; cursor: pointer;
    border-bottom: 1px dashed currentColor;
    transition: color .15s;
}
.lr-user-link:hover { color: var(--lr-accent); }

.lr-bar-cell { display: flex; align-items: center; gap: 8px; justify-content: flex-end; }
.lr-bar-track { width: 72px; height: 6px; background: var(--c-border); border-radius: 3px; overflow: hidden; }
.lr-bar-fill { height: 100%; border-radius: 3px; transition: width .3s; }
.lr-trophy { font-size: 14px; color: #f59e0b; display: inline-flex; }

.lr-empty {
    text-align: center; padding: 40px 20px; color: var(--c-text-4); font-size: 13px;
    margin: 0;
}

/* Section divider */
.lr-divider {
    display: flex; align-items: center; gap: 12px; margin: 32px 0 18px;
}
.lr-divider-line { flex: 1; height: 1px; background: var(--c-border); }
.lr-divider-label {
    font-size: 11px; font-weight: 700;
    text-transform: uppercase; letter-spacing: 1px;
    color: var(--c-text-4); white-space: nowrap;
}

/* Breaks week nav */
.lr-week-nav { display: flex; align-items: center; gap: 6px; }
.lr-week-btn {
    border: 1px solid var(--c-border); background: var(--c-card);
    border-radius: 7px; padding: 4px 10px; font-size: 12px;
    cursor: pointer; font-family: inherit;
    color: var(--c-text-2);
    transition: all .15s;
    display: inline-flex; align-items: center;
}
.lr-week-btn:hover:not(:disabled) { background: var(--c-msg-bg); }
.lr-week-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.lr-week-label { font-size: 12px; font-weight: 600; color: var(--c-text-2); min-width: 150px; text-align: center; font-variant-numeric: tabular-nums; }

/* Priority × Status grid */
.lr-px-table { border-collapse: collapse; }
.lr-px-table thead th {
    padding: 8px 18px; font-size: 11px; color: var(--c-text-4); font-weight: 700;
    text-align: center; text-transform: uppercase; letter-spacing: 0.4px;
    border-bottom: 1px solid var(--c-card-header-border);
}
.lr-px-table thead th:first-child { text-align: left; }
.lr-px-table tbody td {
    padding: 10px 18px; text-align: center; font-size: 14px;
    border-bottom: 1px solid var(--c-card-header-border);
    font-variant-numeric: tabular-nums;
}
.lr-px-table tbody tr:last-child td { border-bottom: none; }
.lr-px-table tbody td:first-child {
    text-align: left; font-weight: 700; font-size: 13px;
}
`;


// ── Subcomponentes ──────────────────────────────────────────────────────────
const tooltipStyle = {
    background: 'var(--c-card)',
    border: '1px solid var(--c-border)',
    borderRadius: 8, fontSize: 12,
    boxShadow: '0 4px 12px rgba(0,0,0,.08)',
    color: 'var(--c-text-1)',
};

function KpiCard({ title, value, color = '#6b7280', subtitle, onClick }) {
    const clickable = typeof onClick === 'function';
    return (
        <div
            className={`lr-kpi${clickable ? ' clickable' : ''}`}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            onClick={clickable ? onClick : undefined}
            onKeyDown={clickable ? (e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }) : undefined}
            style={{ '--kpi-color': color }}
        >
            <p className="lr-kpi-label">{title}</p>
            <p className="lr-kpi-value">{value ?? '—'}</p>
            {subtitle && <p className="lr-kpi-sub">{subtitle}</p>}
        </div>
    );
}

function ChartCard({ title, subtitle, children, height = 220 }) {
    return (
        <div className="lr-card">
            <div className="lr-card-head">
                <p className="lr-card-title">
                    {title}
                    {subtitle && <span className="lr-card-title-sub">{subtitle}</span>}
                </p>
            </div>
            <div className="lr-card-body">
                <ResponsiveContainer width="100%" height={height}>
                    {children}
                </ResponsiveContainer>
            </div>
        </div>
    );
}

function UserNameButton({ userId, name, onSelectUser }) {
    if (!userId || !onSelectUser) return <>{name}</>;
    return (
        <button type="button" className="lr-user-link" onClick={() => onSelectUser(userId)} title="Ver perfil">
            {name}
        </button>
    );
}


// ── Tablas ──────────────────────────────────────────────────────────────────
function SlaTable({ data }) {
    if (!data || data.length === 0) {
        return <p className="lr-empty">Sin datos de SLA en este período.</p>;
    }
    return (
        <table className="lr-table">
            <thead>
                <tr>
                    <th>Departamento</th>
                    <th className="center">Total</th>
                    <th className="center">Cerrados</th>
                    <th className="center">A tiempo</th>
                    <th className="center">Con retraso</th>
                    <th className="center">Cumplimiento</th>
                </tr>
            </thead>
            <tbody>
                {data.map(row => {
                    const pct = row.compliancePct;
                    const color = pct == null ? 'var(--c-text-4)'
                        : pct >= 90 ? '#059669'
                        : pct >= 70 ? '#d97706'
                        : '#dc2626';
                    return (
                        <tr key={row.department}>
                            <td className="strong">{row.department}</td>
                            <td className="center num">{row.total}</td>
                            <td className="center num">{row.closedCount}</td>
                            <td className="center num" style={{ color: '#059669', fontWeight: 700 }}>{row.closedOnTime}</td>
                            <td className="center num" style={{ color: row.closedLate > 0 ? '#dc2626' : 'var(--c-text-4)', fontWeight: row.closedLate > 0 ? 700 : 400 }}>{row.closedLate}</td>
                            <td className="center">
                                {pct != null
                                    ? <span style={{ fontWeight: 700, color }}>{pct}%</span>
                                    : <span style={{ color: 'var(--c-text-4)' }}>—</span>}
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
}

function AgentTable({ data, onSelectUser }) {
    if (!data || data.length === 0) {
        return <p className="lr-empty">Sin agentes con tickets en este período.</p>;
    }
    const withTime = data.filter(r => r.avgResolutionHours != null);
    const maxTime  = withTime.length ? Math.max(...withTime.map(r => r.avgResolutionHours), 1) : 1;

    return (
        <div style={{ overflowX: 'auto' }}>
            <table className="lr-table">
                <thead>
                    <tr>
                        <th>#</th>
                        <th>Agente</th>
                        <th className="center">Asignados</th>
                        <th className="center">Cerrados</th>
                        <th className="center">En progreso</th>
                        <th className="center">Abiertos</th>
                        <th className="center">FCR</th>
                        <th className="center">Resolución prom.</th>
                        <th className="center">CSAT</th>
                        <th className="right">Velocidad</th>
                    </tr>
                </thead>
                <tbody>
                    {data.map((row, i) => {
                        const barW = row.avgResolutionHours != null
                            ? Math.max(6, Math.round((1 - row.avgResolutionHours / maxTime) * 100))
                            : 0;
                        const isFastest = withTime[0]?.name === row.name && withTime.length > 0;
                        return (
                            <tr key={row.name}>
                                <td style={{ color: 'var(--c-text-4)', width: 32 }} className="num">{i + 1}</td>
                                <td className="strong">
                                    <UserNameButton userId={row.userId} name={row.name} onSelectUser={onSelectUser} />
                                </td>
                                <td className="center num">{row.ticketCount}</td>
                                <td className="center num" style={{ color: '#059669', fontWeight: 700 }}>{row.closedCount}</td>
                                <td className="center num" style={{ color: '#d97706' }}>{row.progressCount}</td>
                                <td className="center num" style={{ color: '#2563eb' }}>{row.openCount}</td>
                                <td className="center">
                                    {row.fcr != null ? (
                                        <span style={{
                                            fontWeight: 700,
                                            color: row.fcr >= 90 ? '#059669' : row.fcr >= 70 ? '#d97706' : '#dc2626',
                                        }}>{row.fcr}%</span>
                                    ) : <span style={{ color: 'var(--c-text-4)' }}>—</span>}
                                </td>
                                <td className="center num" style={{ fontWeight: 600 }}>{fmtHours(row.avgResolutionHours)}</td>
                                <td className="center num" style={{ color: row.avgCsat ? '#f59e0b' : 'var(--c-text-4)' }}>{fmtCsat(row.avgCsat)}</td>
                                <td className="right">
                                    {row.avgResolutionHours != null ? (
                                        <div className="lr-bar-cell">
                                            <div className="lr-bar-track">
                                                <div className="lr-bar-fill"
                                                    style={{ width: `${barW}%`, background: isFastest ? '#059669' : 'var(--lr-accent)' }} />
                                            </div>
                                            {isFastest && <span className="lr-trophy">{I.trophy}</span>}
                                        </div>
                                    ) : (
                                        <span style={{ fontSize: 11, color: 'var(--c-text-4)' }}>—</span>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}


// ── Diseño / deliveryDate ───────────────────────────────────────────────────
function DesignMetricsSection({ metrics, onSelectUser }) {
    const m = metrics || {};
    const onTimeColor = m.onTimePct == null ? '#6b7280'
        : m.onTimePct >= 90 ? '#059669'
        : m.onTimePct >= 70 ? '#d97706'
        : '#dc2626';

    return (
        <div className="lr-card">
            <div className="lr-card-head">
                <p className="lr-card-title">
                    Diseño / Producción de materiales
                    <span className="lr-card-title-sub">{(m.departments || []).join(', ') || 'depto en modo fecha de entrega'}</span>
                </p>
            </div>

            <div className="lr-card-body tight" style={{ borderBottom: '1px solid var(--c-card-header-border)' }}>
                <div className="lr-kpi-grid" style={{ marginBottom: 0 }}>
                    <KpiCard title="Tickets totales"   value={m.totalTickets}   color="#2563eb" />
                    <KpiCard title="Materiales totales" value={m.totalMaterials} color="#8b5cf6" subtitle="suma de materiales solicitados" />
                    <KpiCard
                        title="Entregados a tiempo"
                        value={m.onTimePct != null ? `${m.onTimePct}%` : '—'}
                        color={onTimeColor}
                        subtitle={`${m.deliveredOnTime || 0} a tiempo · ${m.deliveredLate || 0} tarde`}
                    />
                    <KpiCard
                        title="Lead time prom."
                        value={m.avgLeadTimeDays != null ? `${m.avgLeadTimeDays} días` : '—'}
                        color="#0891b2"
                        subtitle="creación → entrega"
                    />
                </div>
            </div>

            {m.workloadByDesigner?.length > 0 && (
                <div className="lr-card-body tight" style={{ borderBottom: '1px solid var(--c-card-header-border)' }}>
                    <p className="lr-card-title" style={{ fontSize: 11, color: 'var(--c-text-4)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
                        Carga por diseñador
                    </p>
                    <div style={{ overflowX: 'auto' }}>
                        <table className="lr-table">
                            <thead>
                                <tr>
                                    <th>Diseñador</th>
                                    <th className="center">Activos</th>
                                    <th className="center">Materiales pendientes</th>
                                    <th className="center">Total tickets</th>
                                    <th className="center">A tiempo</th>
                                    <th className="center">Tarde</th>
                                    <th className="center">% a tiempo</th>
                                </tr>
                            </thead>
                            <tbody>
                                {m.workloadByDesigner.map(row => {
                                    const c = row.onTimePct == null ? 'var(--c-text-4)'
                                        : row.onTimePct >= 90 ? '#059669'
                                        : row.onTimePct >= 70 ? '#d97706'
                                        : '#dc2626';
                                    return (
                                        <tr key={(row.name || '') + (row.userId || '')}>
                                            <td className="strong">
                                                <UserNameButton userId={row.userId} name={row.name} onSelectUser={onSelectUser} />
                                            </td>
                                            <td className="center num" style={{ color: row.activeTickets > 0 ? '#d97706' : 'var(--c-text-4)', fontWeight: 700 }}>{row.activeTickets}</td>
                                            <td className="center num" style={{ color: '#8b5cf6', fontWeight: 700 }}>{row.pendingMaterials}</td>
                                            <td className="center num">{row.totalTickets}</td>
                                            <td className="center num" style={{ color: '#059669' }}>{row.deliveredOnTime}</td>
                                            <td className="center num" style={{ color: row.deliveredLate > 0 ? '#dc2626' : 'var(--c-text-4)' }}>{row.deliveredLate}</td>
                                            <td className="center">
                                                {row.onTimePct != null
                                                    ? <span style={{ fontWeight: 700, color: c }}>{row.onTimePct}%</span>
                                                    : <span style={{ color: 'var(--c-text-4)' }}>—</span>}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {m.throughputByWeek?.length > 0 && (
                <div className="lr-card-body">
                    <p style={{ fontSize: 11, color: 'var(--c-text-4)', textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 8px 10px', fontWeight: 700 }}>
                        Throughput por semana
                    </p>
                    <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={m.throughputByWeek} barSize={20}>
                            <CartesianGrid strokeDasharray="3 3" stroke="var(--c-border)" vertical={false} />
                            <XAxis dataKey="week" tick={{ fontSize: 11, fill: 'var(--c-text-3)' }} axisLine={{ stroke: 'var(--c-border)' }} tickLine={false} />
                            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: 'var(--c-text-3)' }} axisLine={{ stroke: 'var(--c-border)' }} tickLine={false} />
                            <Tooltip contentStyle={tooltipStyle} />
                            <Bar dataKey="materials" name="Materiales" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                            <Bar dataKey="tickets"   name="Tickets"    fill={BRAND_BLUE} radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            )}
        </div>
    );
}


// ── Pausas semanales ────────────────────────────────────────────────────────
function weekBoundsFor(date) {
    const d = new Date(date); d.setHours(0, 0, 0, 0);
    const dow = d.getDay();
    const diffToMon = dow === 0 ? -6 : (1 - dow);
    const monday = new Date(d);
    monday.setDate(d.getDate() + diffToMon);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return { from: monday, to: sunday };
}
function fmtIsoDate(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}
function fmtRangeLabel(from, to) {
    const opts = { day: '2-digit', month: 'short' };
    return `${from.toLocaleDateString('es-MX', opts)} – ${to.toLocaleDateString('es-MX', opts)}`;
}

function BreaksSection({ onSelectUser }) {
    const [anchor, setAnchor] = useState(() => weekBoundsFor(new Date()).from);
    const [rows,   setRows]   = useState(null);
    const [loading, setLoading] = useState(false);
    const [err,    setErr]    = useState(null);

    const { from, to } = React.useMemo(() => weekBoundsFor(anchor), [anchor]);

    useEffect(() => {
        let alive = true;
        setLoading(true); setErr(null);
        const params = { from: fmtIsoDate(from), to: fmtIsoDate(to) };
        api.get('/users/break-metrics', { params })
            .then(res => { if (alive) setRows(Array.isArray(res.data) ? res.data : []); })
            .catch(e => { if (alive) setErr(e.response?.data?.error || 'Error al cargar pausas'); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [from, to]);

    const shiftWeek = (delta) => {
        const next = new Date(anchor);
        next.setDate(anchor.getDate() + delta * 7);
        setAnchor(weekBoundsFor(next).from);
    };
    const goToCurrent = () => setAnchor(weekBoundsFor(new Date()).from);
    const isCurrentWeek = fmtIsoDate(weekBoundsFor(new Date()).from) === fmtIsoDate(from);

    return (
        <>
            <div className="lr-divider">
                <div className="lr-divider-line" />
                <span className="lr-divider-label">Pausas de la semana</span>
                <div className="lr-divider-line" />
            </div>

            <div className="lr-card">
                <div className="lr-card-head">
                    <p className="lr-card-title">
                        Comidas y pausas cortas por agente
                        <span className="lr-card-title-sub">cantidad y duración total — registro inicia con el deploy</span>
                    </p>
                    <div className="lr-week-nav">
                        <button className="lr-week-btn" onClick={() => shiftWeek(-1)} title="Semana anterior">{I.chevL}</button>
                        <span className="lr-week-label">{fmtRangeLabel(from, to)}</span>
                        <button className="lr-week-btn" onClick={() => shiftWeek(1)} disabled={isCurrentWeek} title="Semana siguiente">{I.chev}</button>
                        {!isCurrentWeek && (
                            <button className="lr-week-btn" onClick={goToCurrent} style={{ marginLeft: 4 }}>Hoy</button>
                        )}
                    </div>
                </div>

                {loading ? (
                    <div style={{ padding: 18 }}><Skeleton variant="block" height={120} /></div>
                ) : err ? (
                    <div style={{ padding: 18, color: '#dc2626', fontSize: 13 }}>{err}</div>
                ) : !rows || rows.length === 0 ? (
                    <p className="lr-empty">Sin pausas registradas en esta semana.</p>
                ) : (
                    <table className="lr-table">
                        <thead>
                            <tr>
                                <th>Agente</th>
                                <th className="center">Comidas</th>
                                <th className="center">Pausas cortas</th>
                                <th className="center">Personalizadas</th>
                                <th className="center">Total min</th>
                                <th className="center">Promedio</th>
                                <th className="center" title="Pausas que se cerraron por timer (el agente no terminó manualmente)">Auto-expirados</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(r => (
                                <tr key={r.user._id}>
                                    <td className="strong">
                                        <UserNameButton userId={r.user._id} name={r.user.name} onSelectUser={onSelectUser} />
                                    </td>
                                    <td className="center num">{r.lunchCount}</td>
                                    <td className="center num">{r.shortCount}</td>
                                    <td className="center num">{r.otherCount}</td>
                                    <td className="center num" style={{ fontWeight: 700 }}>{r.totalMinutes}</td>
                                    <td className="center num" style={{ color: 'var(--c-text-3)' }}>{r.count > 0 ? `${r.avgMinutes} min` : '—'}</td>
                                    <td className="center num" style={{ color: r.autoExpiredCount > 0 ? '#d97706' : 'var(--c-text-4)' }}>{r.autoExpiredCount}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </>
    );
}


// ── Componente principal ────────────────────────────────────────────────────
export default function LeaderReports() {
    const { user } = useContext(AuthContext);
    const navigate = useNavigate();
    const isAdmin  = user?.role === 'admin';

    const [preset,     setPreset]     = useState('month');
    const [customFrom, setCustomFrom] = useState('');
    const [customTo,   setCustomTo]   = useState('');
    const [data,       setData]       = useState(null);
    const [loading,    setLoading]    = useState(false);
    const [error,      setError]      = useState(null);

    const [analytics,        setAnalytics]        = useState(null);
    const [analyticsLoading, setAnalyticsLoading] = useState(false);
    const [analyticsError,   setAnalyticsError]   = useState(null);

    const [profileUserId, setProfileUserId] = useState(null);

    const abortRef  = useRef(null);
    const rangeRef  = useRef({ from: null, to: null });

    const loadPerformance = useCallback(async (from, to, { silent = false } = {}) => {
        if (abortRef.current) abortRef.current.abort();
        abortRef.current = new AbortController();
        const signal = abortRef.current.signal;

        rangeRef.current = { from, to };
        if (!silent) setLoading(true);
        setError(null);
        try {
            const params = from && to ? { from: from.toISOString(), to: to.toISOString() } : {};
            const res = await api.get('/tickets/leader-reports', { params, signal });
            setData(res.data);
        } catch (e) {
            if (e.name === 'CanceledError' || e.code === 'ERR_CANCELED') return;
            setError(e.response?.data?.error || 'Error al cargar el reporte');
        } finally {
            if (!signal.aborted && !silent) setLoading(false);
        }
    }, []);

    const loadAnalytics = useCallback(async () => {
        if (!isAdmin) return;
        setAnalyticsLoading(true);
        setAnalyticsError(null);
        try {
            const res = await api.get('/tickets/reports');
            setAnalytics(res.data);
        } catch (e) {
            setAnalyticsError(e.response?.data?.error || 'Error al cargar analytics');
        } finally { setAnalyticsLoading(false); }
    }, [isAdmin]);

    useEffect(() => {
        if (preset === 'custom') return;
        const dates = getPresetDates(preset);
        if (dates) loadPerformance(dates.from, dates.to);
    }, [preset, loadPerformance]);

    useEffect(() => { loadAnalytics(); }, [loadAnalytics]);

    useEffect(() => {
        let timer = null;
        const onTicketCreated = () => {
            clearTimeout(timer);
            timer = setTimeout(() => {
                const { from, to } = rangeRef.current;
                if (from && to) loadPerformance(from, to, { silent: true });
            }, 5000);
        };
        socket.on('ticketCreated', onTicketCreated);
        return () => {
            socket.off('ticketCreated', onTicketCreated);
            clearTimeout(timer);
        };
    }, [loadPerformance]);

    const handlePreset = (key) => setPreset(key);
    const handleCustomApply = () => {
        if (!customFrom || !customTo) return;
        loadPerformance(new Date(customFrom), new Date(customTo));
    };

    const total    = data?.total ?? 0;
    const closed   = data?.byStatus?.find(s => s.status === 'closed')?.count      ?? 0;
    const open     = data?.byStatus?.find(s => s.status === 'open')?.count         ?? 0;
    const progress = data?.byStatus?.find(s => s.status === 'in_progress')?.count  ?? 0;
    const overdue  = data?.overdue ?? 0;
    const withTime = (data?.agentEfficiency || []).filter(a => a.avgResolutionHours != null);
    const avgRes   = withTime.length
        ? withTime.reduce((s, a) => s + a.avgResolutionHours, 0) / withTime.length
        : null;

    const trend = data ? fillDays(data.trendByDay, data.fromDate, data.toDate) : [];
    const periodLabel = data ? `${fmtDate(data.fromDate)} — ${fmtDate(data.toDate)}` : '';

    const volumeData = analytics ? (analytics.volumeLast6Months || []).map(d => ({ ...d, mes: fmtMonth(d.month) })) : [];
    const hourData   = analytics?.byHourOfDay  || [];
    const dowData    = analytics?.byDayOfWeek  || [];
    const maxHour    = Math.max(...hourData.map(h => h.count), 1);
    const hourColor  = (count) => {
        const intensity = count / maxHour;
        const r = Math.round(10  + intensity * (220 - 10));
        const g = Math.round(74  + intensity * (40  - 74));
        const b = Math.round(168 + intensity * (40  - 168));
        return `rgb(${r},${g},${b})`;
    };

    return (
        <>
            <style>{CSS}</style>
            <div className="lr-root">

                <div className="lr-header">
                    <div>
                        <h1 className="lr-title">Rendimiento y Reportes</h1>
                        <p className="lr-subtitle">
                            Desempeño de agentes{isAdmin ? ' · métricas avanzadas del sistema' : ' de tu departamento'}
                            {periodLabel && <span className="lr-period">· {periodLabel}</span>}
                        </p>
                    </div>
                    <div className="lr-actions">
                        <button type="button" className="lr-btn" disabled={loading}
                            onClick={() => {
                                const { from, to } = rangeRef.current;
                                if (from && to) loadPerformance(from, to);
                                else if (preset !== 'custom') {
                                    const dates = getPresetDates(preset);
                                    if (dates) loadPerformance(dates.from, dates.to);
                                }
                            }}
                        >
                            {loading ? <Spinner size="sm" animation="border" /> : <>{I.refresh} Actualizar</>}
                        </button>
                        {isAdmin && analytics && (
                            <button type="button" className="lr-btn" onClick={loadAnalytics}>
                                Actualizar analytics
                            </button>
                        )}
                    </div>
                </div>

                <div className="lr-filters">
                    <div className="lr-presets">
                        {PRESETS.map(p => (
                            <button key={p.key} className={`lr-preset${preset === p.key ? ' active' : ''}`} onClick={() => handlePreset(p.key)}>
                                {p.label}
                            </button>
                        ))}
                    </div>
                    {preset === 'custom' && (
                        <div className="lr-custom-range">
                            <input type="date" className="lr-date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} />
                            <span style={{ color: 'var(--c-text-4)', fontSize: 13 }}>—</span>
                            <input type="date" className="lr-date" value={customTo} min={customFrom} onChange={e => setCustomTo(e.target.value)} />
                            <button type="button" className="lr-btn lr-btn-primary" onClick={handleCustomApply} disabled={!customFrom || !customTo || loading}>
                                Aplicar
                            </button>
                        </div>
                    )}
                </div>

                {loading && (
                    <div>
                        <div className="lr-kpi-grid">
                            {Array.from({ length: 8 }).map((_, i) => <SkeletonKpi key={i} />)}
                        </div>
                        <div className="lr-card">
                            <div className="lr-card-body tight">
                                <Skeleton variant="line" width="30%" height={14} />
                                <div style={{ marginTop: 14 }}>
                                    {Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} withAvatar />)}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                {error && !loading && (
                    <div style={{ textAlign: 'center', padding: '40px 0' }}>
                        <p style={{ color: '#dc2626' }}>{error}</p>
                    </div>
                )}

                {!loading && !error && data && (
                    <>
                        <div className="lr-kpi-grid">
                            <KpiCard title="Total tickets"     value={total}    color="#2563eb" />
                            <KpiCard title="Cerrados"          value={closed}   color="#059669" />
                            <KpiCard title="En progreso"       value={progress} color="#d97706" />
                            <KpiCard title="Abiertos"          value={open}     color="#6b7280" />
                            <KpiCard title="Vencidos"          value={overdue}  color={overdue > 0 ? '#dc2626' : '#6b7280'} subtitle="del período, sin cerrar" />
                            <KpiCard title="Resolución prom."  value={fmtHours(avgRes)} color="#8b5cf6" subtitle="promedio del período" />
                            <KpiCard title="1ª respuesta prom."     value={fmtHours(data.avgFirstResponseHours)} color="#2563eb" subtitle="hasta 1er comentario" />
                            <KpiCard
                                title="CSAT promedio"
                                value={fmtCsat(data.avgCsat)}
                                color={data.avgCsat == null ? '#6b7280' : data.avgCsat >= 4 ? '#059669' : data.avgCsat >= 3 ? '#d97706' : '#dc2626'}
                                subtitle={data.csatCount > 0 ? `${data.csatCount} respuestas` : 'sin respuestas'}
                            />
                            <KpiCard
                                title="Cerrados al 1er intento"
                                value={data.closedFirstTime ?? 0}
                                color="#059669"
                                subtitle={data.totalEverClosed > 0
                                    ? `${Math.round((data.closedFirstTime / data.totalEverClosed) * 1000) / 10}% de los cerrados`
                                    : 'sin datos'}
                            />
                            <KpiCard
                                title="Reabiertos y re-cerrados"
                                value={data.reopenedAndClosed ?? 0}
                                color="#d97706"
                                subtitle="Ver lista →"
                                onClick={() => navigate('/tickets?reopened=1&status=closed')}
                            />
                            <KpiCard
                                title="Reabiertos pendientes"
                                value={data.reopenedNotClosed ?? 0}
                                color={data.reopenedNotClosed > 0 ? '#dc2626' : '#6b7280'}
                                subtitle="Ver lista →"
                                onClick={() => navigate('/tickets?reopened=1')}
                            />
                            <KpiCard
                                title="Tasa de reapertura"
                                value={`${data.reopenRate ?? 0}%`}
                                color={(data.reopenRate ?? 0) > 15 ? '#dc2626' : (data.reopenRate ?? 0) > 5 ? '#d97706' : '#059669'}
                                subtitle={`${data.reopenedCount ?? 0} de ${data.totalEverClosed ?? 0} cerrados`}
                            />
                        </div>

                        {trend.length > 0 && (
                            <ChartCard title="Tickets creados — tendencia diaria" height={200}>
                                <LineChart data={trend}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="var(--c-border)" vertical={false} />
                                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: 'var(--c-text-3)' }} axisLine={{ stroke: 'var(--c-border)' }} tickLine={false} interval="preserveStartEnd" />
                                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: 'var(--c-text-3)' }} axisLine={{ stroke: 'var(--c-border)' }} tickLine={false} />
                                    <Tooltip contentStyle={tooltipStyle} />
                                    <Line type="monotone" dataKey="count" name="Tickets" stroke={BRAND_BLUE} strokeWidth={2.5}
                                        dot={{ r: 3, fill: BRAND_BLUE, strokeWidth: 0 }}
                                        activeDot={{ r: 5, fill: BRAND_BLUE, strokeWidth: 2, stroke: 'var(--c-card)' }} />
                                </LineChart>
                            </ChartCard>
                        )}

                        {data.byStatus?.length > 0 && (
                            <div className="lr-cols-2">
                                <div className="lr-card">
                                    <div className="lr-card-head"><p className="lr-card-title">Tickets por estado</p></div>
                                    <div className="lr-card-body">
                                        <ResponsiveContainer width="100%" height={170}>
                                            <BarChart data={[
                                                { name: 'Abierto',     count: open },
                                                { name: 'En progreso', count: progress },
                                                { name: 'Cerrado',     count: closed },
                                            ]} barSize={38}>
                                                <CartesianGrid strokeDasharray="3 3" stroke="var(--c-border)" vertical={false} />
                                                <XAxis dataKey="name" tick={{ fontSize: 12, fill: 'var(--c-text-3)' }} axisLine={{ stroke: 'var(--c-border)' }} tickLine={false} />
                                                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: 'var(--c-text-3)' }} axisLine={{ stroke: 'var(--c-border)' }} tickLine={false} />
                                                <Tooltip contentStyle={tooltipStyle} cursor={{ fill: 'rgba(10, 74, 168, .06)' }} />
                                                <Bar dataKey="count" name="Tickets" radius={[6, 6, 0, 0]}>
                                                    {[open, progress, closed].map((_, i) => (
                                                        <Cell key={i} fill={['#2563eb', '#d97706', '#059669'][i]} />
                                                    ))}
                                                </Bar>
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>
                                <div className="lr-card">
                                    <div className="lr-card-head"><p className="lr-card-title">Tickets cerrados por agente</p></div>
                                    <div className="lr-card-body">
                                        <ResponsiveContainer width="100%" height={170}>
                                            <BarChart
                                                layout="vertical"
                                                data={[...data.agentEfficiency].sort((a, b) => b.closedCount - a.closedCount).slice(0, 6)}
                                                margin={{ left: 10 }}
                                                barSize={16}
                                            >
                                                <CartesianGrid strokeDasharray="3 3" stroke="var(--c-border)" horizontal={false} />
                                                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: 'var(--c-text-3)' }} axisLine={{ stroke: 'var(--c-border)' }} tickLine={false} />
                                                <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: 'var(--c-text-2)' }} axisLine={false} tickLine={false} width={90} />
                                                <Tooltip contentStyle={tooltipStyle} cursor={{ fill: 'rgba(5, 150, 105, .08)' }} />
                                                <Bar dataKey="closedCount" name="Cerrados" fill="#059669" radius={[0, 6, 6, 0]} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="lr-card">
                            <div className="lr-card-head">
                                <p className="lr-card-title">
                                    Rendimiento por agente
                                    <span className="lr-card-title-sub">ordenado por tickets cerrados</span>
                                </p>
                            </div>
                            <AgentTable data={data.agentEfficiency} onSelectUser={setProfileUserId} />
                        </div>

                        {data.designMetrics && data.designMetrics.totalTickets > 0 && (
                            <DesignMetricsSection metrics={data.designMetrics} onSelectUser={setProfileUserId} />
                        )}

                        {data.slaByDepartment?.length > 0 && (
                            <div className="lr-card">
                                <div className="lr-card-head">
                                    <p className="lr-card-title">
                                        Cumplimiento SLA por departamento
                                        <span className="lr-card-title-sub">tickets cerrados dentro del plazo vs. con retraso</span>
                                    </p>
                                </div>
                                <SlaTable data={data.slaByDepartment} />
                            </div>
                        )}
                    </>
                )}

                <BreaksSection onSelectUser={setProfileUserId} />

                {isAdmin && (
                    <>
                        <div className="lr-divider">
                            <div className="lr-divider-line" />
                            <span className="lr-divider-label">Analytics avanzado</span>
                            <div className="lr-divider-line" />
                        </div>

                        {analyticsLoading && (
                            <div>
                                <div className="lr-card">
                                    <div className="lr-card-body tight">
                                        <Skeleton variant="line" width="30%" height={14} />
                                        <div style={{ marginTop: 14 }}><Skeleton variant="block" height={220} /></div>
                                    </div>
                                </div>
                                <div className="lr-cols-2">
                                    <div className="lr-card"><div className="lr-card-body"><Skeleton variant="block" height={180} /></div></div>
                                    <div className="lr-card"><div className="lr-card-body"><Skeleton variant="block" height={180} /></div></div>
                                </div>
                            </div>
                        )}
                        {analyticsError && !analyticsLoading && (
                            <div style={{ textAlign: 'center', padding: '24px 0' }}>
                                <p style={{ color: '#dc2626', marginBottom: 12 }}>{analyticsError}</p>
                                <button type="button" className="lr-btn" onClick={loadAnalytics}>Reintentar</button>
                            </div>
                        )}

                        {!analyticsLoading && !analyticsError && analytics && (
                            <>
                                <ChartCard title="Volumen de tickets — últimos 6 meses" height={200}>
                                    <BarChart data={volumeData} barSize={36}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="var(--c-border)" vertical={false} />
                                        <XAxis dataKey="mes" tick={{ fontSize: 12, fill: 'var(--c-text-3)' }} axisLine={{ stroke: 'var(--c-border)' }} tickLine={false} />
                                        <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: 'var(--c-text-3)' }} axisLine={{ stroke: 'var(--c-border)' }} tickLine={false} />
                                        <Tooltip contentStyle={tooltipStyle} cursor={{ fill: 'rgba(10, 74, 168, .06)' }} />
                                        <Bar dataKey="count" name="Tickets" fill={BRAND_BLUE} radius={[6, 6, 0, 0]} />
                                    </BarChart>
                                </ChartCard>

                                <div className="lr-cols-eq">
                                    <div className="lr-card">
                                        <div className="lr-card-head"><p className="lr-card-title">Tickets por hora del día</p></div>
                                        <div className="lr-card-body">
                                            <ResponsiveContainer width="100%" height={200}>
                                                <BarChart data={hourData} barSize={14}>
                                                    <CartesianGrid strokeDasharray="3 3" stroke="var(--c-border)" vertical={false} />
                                                    <XAxis dataKey="hour" tickFormatter={h => `${h}h`} tick={{ fontSize: 10, fill: 'var(--c-text-3)' }} axisLine={{ stroke: 'var(--c-border)' }} tickLine={false} interval={1} />
                                                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: 'var(--c-text-3)' }} axisLine={{ stroke: 'var(--c-border)' }} tickLine={false} />
                                                    <Tooltip contentStyle={tooltipStyle} formatter={(v) => [v, 'Tickets']} labelFormatter={l => `${l}:00 h`} />
                                                    <Bar dataKey="count" name="Tickets" radius={[3, 3, 0, 0]}>
                                                        {hourData.map((entry, i) => (
                                                            <Cell key={i} fill={hourColor(entry.count)} />
                                                        ))}
                                                    </Bar>
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </div>
                                    <div className="lr-card">
                                        <div className="lr-card-head"><p className="lr-card-title">Tickets por día de semana</p></div>
                                        <div className="lr-card-body">
                                            <ResponsiveContainer width="100%" height={200}>
                                                <BarChart data={dowData} barSize={28}>
                                                    <CartesianGrid strokeDasharray="3 3" stroke="var(--c-border)" vertical={false} />
                                                    <XAxis dataKey="day" tick={{ fontSize: 12, fill: 'var(--c-text-3)' }} axisLine={{ stroke: 'var(--c-border)' }} tickLine={false} />
                                                    <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: 'var(--c-text-3)' }} axisLine={{ stroke: 'var(--c-border)' }} tickLine={false} />
                                                    <Tooltip contentStyle={tooltipStyle} cursor={{ fill: 'rgba(139, 92, 246, .08)' }} />
                                                    <Bar dataKey="count" name="Tickets" fill="#8b5cf6" radius={[6, 6, 0, 0]} />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </div>
                                </div>

                                {analytics.byPriorityAndStatus?.length > 0 && (
                                    <div className="lr-card">
                                        <div className="lr-card-head"><p className="lr-card-title">Distribución prioridad × estado</p></div>
                                        <div className="lr-card-body tight" style={{ overflowX: 'auto' }}>
                                            <table className="lr-px-table" style={{ minWidth: 400 }}>
                                                <thead>
                                                    <tr>
                                                        <th>Prioridad</th>
                                                        {['open', 'in_progress', 'closed'].map(s => (
                                                            <th key={s}>{{ open: 'Abierto', in_progress: 'En progreso', closed: 'Cerrado' }[s]}</th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {['low', 'medium', 'high', 'urgent'].map(p => (
                                                        <tr key={p}>
                                                            <td style={{ color: PRIO_COLOR[p] }}>{PRIO_LABEL[p]}</td>
                                                            {['open', 'in_progress', 'closed'].map(s => {
                                                                const entry = analytics.byPriorityAndStatus.find(x => x.priority === p && x.status === s);
                                                                return (
                                                                    <td key={s} style={{ fontWeight: entry?.count ? 700 : 400, color: entry?.count ? 'var(--c-text-1)' : 'var(--c-text-4)' }}>
                                                                        {entry?.count ?? 0}
                                                                    </td>
                                                                );
                                                            })}
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </>
                )}

                <UserProfileModal
                    userId={profileUserId}
                    onClose={() => setProfileUserId(null)}
                />
            </div>
        </>
    );
}
